<template>
  <div>
    <!-- Header -->
    <div class="card mb-5">
      <div class="card-header border-0 pt-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label fw-bold fs-3 mb-1">Pengaturan IoT Camera</span>
          <span class="text-muted mt-1 fw-semibold fs-7">
            Konfigurasi model deteksi dan parameter preprocessing per perangkat
          </span>
        </h3>
        <div class="card-toolbar gap-2">
          <button @click="exportConfig" class="btn btn-sm btn-light-info">
            <i class="bi bi-download me-1"></i> Export config.json
          </button>
          <button @click="resetToDefault" class="btn btn-sm btn-light-warning">
            <i class="bi bi-arrow-counterclockwise me-1"></i> Reset Default
          </button>
          <button @click="saveSettings" class="btn btn-sm btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-save me-1"></i>
            {{ saving ? 'Menyimpan...' : 'Simpan' }}
          </button>
        </div>
      </div>

      <!-- Device Selector -->
      <div class="card-body pt-0 pb-5">
        <div class="row align-items-center">
          <div class="col-md-4">
            <label class="form-label fw-bold">Pilih Perangkat</label>
            <select v-model="selectedDeviceId" class="form-select" @change="onDeviceChange">
              <option value="global">Global (Semua Perangkat)</option>
              <option v-for="device in devices" :key="device.id" :value="device.id">
                #{{ device.id }} — {{ device.name || 'Unnamed' }}
                {{ device.location ? '· ' + device.location : '' }}
              </option>
            </select>
          </div>
          <div class="col-md-8 mt-4">
            <div v-if="selectedDeviceId !== 'global'" class="d-flex gap-3">
              <span class="badge badge-light-primary fs-7">
                <i class="bi bi-cpu me-1"></i>ID: {{ selectedDeviceId }}
              </span>
              <span :class="`badge badge-light-${currentModel === 'smart_motion' || currentModel === 'smart_cascade' ? 'primary' : currentModel === 'contour' ? 'warning' : currentModel === 'contour_motion' ? 'danger' : currentModel === 'yolo11n_coco' ? 'info' : 'success'} fs-7`">
                <i :class="`bi bi-${currentModel === 'smart_motion' || currentModel === 'smart_cascade' ? 'stars' : currentModel === 'contour' || currentModel === 'contour_motion' ? 'layers' : 'robot'} me-1`"></i>
                Model: {{ currentModel === 'smart_motion' ? 'Walet Cerdas' : currentModel === 'smart_cascade' ? 'Walet Cerdas AI' : currentModel === 'yolov8n' ? 'YOLOv8n' : currentModel === 'yolo11n_coco' ? 'YOLO11n COCO' : currentModel === 'contour_motion' ? 'Contour Motion' : 'Contour Tradisional' }}
              </span>
            </div>
            <div v-else class="text-muted fs-7">
              <i class="bi bi-info-circle me-1"></i>
              Pengaturan global akan digunakan oleh semua perangkat yang tidak memiliki konfigurasi khusus.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings Tabs -->
    <div class="card">
      <div class="card-header border-0 pt-0">
        <ul class="nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 fw-semibold" id="settingsTabs">
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'model' }" @click="activeTab = 'model'" href="#">
              <i class="bi bi-robot me-1"></i> Model
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'preprocessing' }" @click="activeTab = 'preprocessing'" href="#">
              <i class="bi bi-sliders me-1"></i> Preprocessing Warna
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'detection' }" @click="activeTab = 'detection'" href="#">
              <i class="bi bi-bounding-box me-1"></i> Deteksi
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'tracker' }" @click="activeTab = 'tracker'" href="#">
              <i class="bi bi-arrows-move me-1"></i> Tracker
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'counting' }" @click="activeTab = 'counting'" href="#">
              <i class="bi bi-123 me-1"></i> Counting
            </a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'background' }" @click="activeTab = 'background'" href="#">
              <i class="bi bi-layers me-1"></i> Background
            </a>
          </li>
        </ul>
      </div>

      <div class="card-body">

        <!-- ── MODEL TAB ── -->
        <div v-show="activeTab === 'model'">
          <div class="row g-5">

            <!-- Model Toggle -->
            <div class="col-12">
              <div class="notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-5">
                <i class="bi bi-info-circle fs-2tx text-primary me-4"></i>
                <div>
                  <h5 class="mb-1">Pilih Model Deteksi</h5>
                  <p class="mb-0 fs-7 text-gray-600">
                    <b>Walet Cerdas</b> (disarankan): Deteksi gerak + filter bentuk &amp; ukuran + validasi lintasan, sehingga fokus menghitung burung walet secara akurat. Ringan dan hemat sumber daya (berjalan tanpa GPU).<br>
                    <b>Walet Cerdas AI</b>: Walet Cerdas + verifikasi AI per-objek (model ONNX kecil). Akurasi tertinggi.<br>
                    <b>YOLOv8n</b>: Model deep learning terlatih pada dataset walet (mAP50=90.7%). Paling akurat untuk walet.<br>
                    <b>YOLO11n COCO</b>: Model umum deteksi burung dari dataset COCO. Untuk benchmarking — bandingkan performa vs model khusus walet.<br>
                    <b>Contour Tradisional</b>: MOG2 background subtraction + analisis kontur + validasi bentuk (solidity, aspect ratio, dll).<br>
                    <b>Contour Motion</b>: Khusus burung walet berukuran kecil — sangat sensitif mendeteksi setiap titik gerak. Cocok jika walet tampak sebagai titik kecil hitam/putih di kamera.
                  </p>
                </div>
              </div>

              <!-- Walet Cerdas (recommended) — the shape-aware detectors -->
              <div class="d-flex gap-4 mb-4">
                <div
                  @click="currentModel = 'smart_motion'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'smart_motion' ? 'border-primary bg-light-primary' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'smart_motion' ? 'bg-primary' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-stars text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">Walet Cerdas</div>
                    <div class="text-gray-500 fs-7">Gerak + Bentuk + Ukuran · Ringan, tanpa GPU</div>
                    <div class="text-gray-400 fs-8">Fokus menghitung burung walet</div>
                  </div>
                  <div class="ms-auto text-end">
                    <span v-if="currentModel === 'smart_motion'" class="badge badge-primary">Aktif</span>
                    <span class="badge badge-light-primary ms-1 fs-9">Disarankan</span>
                  </div>
                </div>

                <div
                  @click="currentModel = 'smart_cascade'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'smart_cascade' ? 'border-primary bg-light-primary' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'smart_cascade' ? 'bg-primary' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-robot text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">Walet Cerdas AI</div>
                    <div class="text-gray-500 fs-7">Cascade + verifikasi AI per-objek</div>
                    <div class="text-gray-400 fs-8">Model: swiftlet_patch_clf.onnx</div>
                  </div>
                  <div class="ms-auto text-end">
                    <span v-if="currentModel === 'smart_cascade'" class="badge badge-primary">Aktif</span>
                    <span class="badge badge-light-info ms-1 fs-9">AI</span>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-4">
                <div
                  @click="currentModel = 'yolov8n'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'yolov8n' ? 'border-success bg-light-success' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'yolov8n' ? 'bg-success' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-robot text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">YOLOv8n</div>
                    <div class="text-gray-500 fs-7">Deep Learning · mAP50: 90.7%</div>
                    <div class="text-gray-400 fs-8">Model: swiftlet_yolov8n.onnx</div>
                  </div>
                  <div class="ms-auto">
                    <span v-if="currentModel === 'yolov8n'" class="badge badge-success">Aktif</span>
                  </div>
                </div>

                <div
                  @click="currentModel = 'yolo11n_coco'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'yolo11n_coco' ? 'border-info bg-light-info' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'yolo11n_coco' ? 'bg-info' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-cpu text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">YOLO11n COCO</div>
                    <div class="text-gray-500 fs-7">Deep Learning · General Bird (COCO)</div>
                    <div class="text-gray-400 fs-8">Model: yolo11n_bird.onnx</div>
                  </div>
                  <div class="ms-auto">
                    <span v-if="currentModel === 'yolo11n_coco'" class="badge badge-info">Aktif</span>
                    <span class="badge badge-light-secondary ms-1 fs-9">Benchmark</span>
                  </div>
                </div>

                <div
                  @click="currentModel = 'contour'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'contour' ? 'border-warning bg-light-warning' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'contour' ? 'bg-warning' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-layers text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">Contour Tradisional</div>
                    <div class="text-gray-500 fs-7">MOG2 + OpenCV · Ringan</div>
                    <div class="text-gray-400 fs-8">Tidak memerlukan ONNX model</div>
                  </div>
                  <div class="ms-auto">
                    <span v-if="currentModel === 'contour'" class="badge badge-warning">Aktif</span>
                  </div>
                </div>

                <div
                  @click="currentModel = 'contour_motion'"
                  :class="`card card-bordered cursor-pointer flex-row align-items-center p-5 gap-4 flex-1 ${currentModel === 'contour_motion' ? 'border-danger bg-light-danger' : 'border-gray-300'}`"
                  style="cursor:pointer"
                >
                  <div :class="`symbol symbol-50px symbol-circle ${currentModel === 'contour_motion' ? 'bg-danger' : 'bg-secondary'}`">
                    <span class="symbol-label text-white fs-3"><i class="bi bi-wind text-white"></i></span>
                  </div>
                  <div>
                    <div class="fw-bold fs-5">Contour Motion</div>
                    <div class="text-gray-500 fs-7">Motion + Blob · Paling Sensitif</div>
                    <div class="text-gray-400 fs-8">Sangat sensitif untuk walet berukuran titik kecil</div>
                  </div>
                  <div class="ms-auto">
                    <span v-if="currentModel === 'contour_motion'" class="badge badge-danger">Aktif</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contour Motion Settings -->
            <template v-if="currentModel === 'contour_motion'">
              <div class="col-12"><div class="separator separator-dashed my-2"></div><h5 class="mb-0">Parameter Contour Motion</h5></div>

              <div class="col-md-4">
                <label class="form-label fw-semibold">Area Minimum Blob (px²)
                  <span class="badge badge-light-primary ms-2">{{ config.cm_min_area }}</span>
                </label>
                <input type="range" class="form-range" min="5" max="100" step="1"
                  v-model.number="config.cm_min_area" />
                <div class="d-flex justify-content-between text-muted fs-8"><span>5 (sangat kecil)</span><span>100 (medium)</span></div>
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold">Area Maksimum Blob (px²)
                  <span class="badge badge-light-primary ms-2">{{ config.cm_max_area }}</span>
                </label>
                <input type="range" class="form-range" min="100" max="3000" step="50"
                  v-model.number="config.cm_max_area" />
                <div class="d-flex justify-content-between text-muted fs-8"><span>100</span><span>3000 (besar)</span></div>
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold">Frame Diff Threshold
                  <span class="badge badge-light-primary ms-2">{{ config.cm_diff_threshold }}</span>
                </label>
                <input type="range" class="form-range" min="5" max="60" step="1"
                  v-model.number="config.cm_diff_threshold" />
                <div class="d-flex justify-content-between text-muted fs-8"><span>5 (sensitif)</span><span>60 (ketat)</span></div>
              </div>
            </template>

            <!-- YOLO Settings (untuk semua model YOLO) -->
            <template v-if="currentModel === 'yolov8n' || currentModel === 'yolo11n_coco'">
              <div class="col-12"><div class="separator separator-dashed my-2"></div><h5 class="mb-0">Parameter YOLO {{ currentModel === 'yolo11n_coco' ? '(YOLO11n COCO)' : '(YOLOv8n)' }}</h5></div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Confidence Threshold YOLO
                  <span class="text-muted fs-8 ms-1">({{ config.yolo_conf_threshold }})</span>
                </label>
                <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                  v-model.number="config.yolo_conf_threshold" />
                <div class="d-flex justify-content-between text-muted fs-8"><span>0.1 (sensitif)</span><span>0.9 (ketat)</span></div>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">NMS IoU Threshold YOLO
                  <span class="text-muted fs-8 ms-1">({{ config.yolo_nms_threshold }})</span>
                </label>
                <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                  v-model.number="config.yolo_nms_threshold" />
                <div class="d-flex justify-content-between text-muted fs-8"><span>0.1 (strict)</span><span>0.9 (relaxed)</span></div>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Input Size YOLO (px)
                  <span class="text-muted fs-8 ms-1">({{ config.yolo_input_size }})</span>
                </label>
                <select v-model.number="config.yolo_input_size" class="form-select">
                  <option :value="320">320 × 320 (cepat)</option>
                  <option :value="416">416 × 416</option>
                  <option :value="640">640 × 640 (akurat)</option>
                </select>
              </div>
            </template>
          </div>
        </div>

        <!-- ── PREPROCESSING TAB ── -->
        <div v-show="activeTab === 'preprocessing'">
          <div class="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-6">
            <i class="bi bi-palette fs-2tx text-warning me-4"></i>
            <div>
              <h5 class="mb-1">Preprocessing Warna</h5>
              <p class="mb-0 fs-7 text-gray-600">
                Penyesuaian warna sebelum deteksi. Berguna di kondisi pencahayaan tidak ideal (gelap, terlalu terang, atau kamera berwarna berbeda).
                Nilai 0 = tidak ada perubahan.
              </p>
            </div>
          </div>

          <div class="row g-5">
            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-sun me-1"></i> Brightness (Kecerahan)
                <span class="badge badge-light-primary ms-2">{{ config.color_brightness > 0 ? '+' : '' }}{{ config.color_brightness }}</span>
              </label>
              <input type="range" class="form-range" min="-100" max="100" step="5"
                v-model.number="config.color_brightness" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>-100 (gelap)</span><span>0</span><span>+100 (terang)</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-circle-half me-1"></i> Contrast (Kontras)
                <span class="badge badge-light-primary ms-2">{{ config.color_contrast > 0 ? '+' : '' }}{{ config.color_contrast }}%</span>
              </label>
              <input type="range" class="form-range" min="-100" max="100" step="5"
                v-model.number="config.color_contrast" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>-100 (flat)</span><span>0</span><span>+100 (tinggi)</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-droplet-half me-1"></i> Saturation (Saturasi)
                <span class="badge badge-light-primary ms-2">{{ config.color_saturation > 0 ? '+' : '' }}{{ config.color_saturation }}%</span>
              </label>
              <input type="range" class="form-range" min="-100" max="100" step="5"
                v-model.number="config.color_saturation" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>-100 (grayscale)</span><span>0</span><span>+100 (vivid)</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-palette me-1"></i> Hue Shift (Geser Warna)
                <span class="badge badge-light-primary ms-2">{{ config.color_hue > 0 ? '+' : '' }}{{ config.color_hue }}°</span>
              </label>
              <input type="range" class="form-range" min="-180" max="180" step="10"
                v-model.number="config.color_hue" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>-180°</span><span>0°</span><span>+180°</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-shadows me-1"></i> Gamma Correction
                <span class="badge badge-light-primary ms-2">{{ config.color_gamma }}</span>
              </label>
              <input type="range" class="form-range" min="0.3" max="3.0" step="0.1"
                v-model.number="config.color_gamma" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>0.3 (gelap)</span><span>1.0 (normal)</span><span>3.0 (terang)</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">
                <i class="bi bi-symmetry-horizontal me-1"></i> Clahe (Adaptive Equalization)
              </label>
              <div class="form-check form-switch form-check-custom form-check-solid mt-2">
                <input class="form-check-input" type="checkbox" v-model="config.color_use_clahe" />
                <label class="form-check-label fw-semibold text-gray-700">
                  {{ config.color_use_clahe ? 'Aktif — meningkatkan kontras lokal' : 'Nonaktif' }}
                </label>
              </div>
              <template v-if="config.color_use_clahe">
                <div class="mt-3">
                  <label class="form-label fs-8">Clip Limit: {{ config.color_clahe_clip }}</label>
                  <input type="range" class="form-range" min="1" max="10" step="0.5"
                    v-model.number="config.color_clahe_clip" />
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ── DETECTION TAB ── -->
        <div v-show="activeTab === 'detection'">
          <div class="row g-5">

            <div class="col-12"><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Ukuran Kontur</h6></div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Min Contour Area (px²)
                <span class="badge badge-light-primary ms-2">{{ config.min_contour_area }}</span>
              </label>
              <input type="range" class="form-range" min="20" max="1000" step="10"
                v-model.number="config.min_contour_area" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>20</span><span>1000</span></div>
              <div class="text-muted fs-8 mt-1">Objek lebih kecil dari ini diabaikan. Turunkan jika burung terlewat.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Max Contour Area (px²)
                <span class="badge badge-light-primary ms-2">{{ config.max_contour_area }}</span>
              </label>
              <input type="range" class="form-range" min="1000" max="30000" step="500"
                v-model.number="config.max_contour_area" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>1000</span><span>30000</span></div>
              <div class="text-muted fs-8 mt-1">Objek lebih besar dari ini diabaikan. Naikkan jika burung besar terlewat.</div>
            </div>

            <div class="col-12"><div class="separator separator-dashed my-2"></div><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Bentuk & Aspek Rasio</h6></div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Aspect Ratio Min
                <span class="badge badge-light-primary ms-2">{{ config.shape_aspect_min }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="1.0" step="0.05"
                v-model.number="config.shape_aspect_min" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>0.1</span><span>1.0</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Aspect Ratio Max
                <span class="badge badge-light-primary ms-2">{{ config.shape_aspect_max }}</span>
              </label>
              <input type="range" class="form-range" min="1.0" max="6.0" step="0.1"
                v-model.number="config.shape_aspect_max" />
              <div class="d-flex justify-content-between text-muted fs-8"><span>1.0</span><span>6.0</span></div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Min Solidity
                <span class="badge badge-light-primary ms-2">{{ config.shape_min_solidity }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                v-model.number="config.shape_min_solidity" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Min Compactness
                <span class="badge badge-light-primary ms-2">{{ config.shape_min_compactness }}</span>
              </label>
              <input type="range" class="form-range" min="0.01" max="0.5" step="0.01"
                v-model.number="config.shape_min_compactness" />
            </div>

            <div class="col-12"><div class="separator separator-dashed my-2"></div><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Confidence Scoring</h6></div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Confidence Threshold
                <span class="badge badge-light-primary ms-2">{{ config.confidence_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                v-model.number="config.confidence_threshold" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Display Confidence Threshold
                <span class="badge badge-light-primary ms-2">{{ config.display_confidence_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                v-model.number="config.display_confidence_threshold" />
            </div>

            <div class="col-12"><div class="separator separator-dashed my-2"></div><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Bobot Confidence (harus total = 1.0)</h6></div>

            <div class="col-md-4" v-for="(label, key) in confidenceWeights" :key="key">
              <label class="form-label fw-semibold fs-8">{{ label }}
                <span class="badge badge-light-info ms-1">{{ config[key] }}</span>
              </label>
              <input type="range" class="form-range" min="0" max="0.5" step="0.05"
                v-model.number="config[key]" />
            </div>
            <div class="col-12">
              <div :class="`alert alert-${Math.abs(totalConfidenceWeight - 1.0) < 0.01 ? 'success' : 'warning'} py-2 fs-8`">
                <i :class="`bi bi-${Math.abs(totalConfidenceWeight - 1.0) < 0.01 ? 'check-circle' : 'exclamation-triangle'} me-1`"></i>
                Total bobot: <strong>{{ totalConfidenceWeight.toFixed(2) }}</strong>
                {{ Math.abs(totalConfidenceWeight - 1.0) < 0.01 ? '✓ Valid' : '⚠ Harus = 1.0' }}
              </div>
            </div>
          </div>
        </div>

        <!-- ── TRACKER TAB ── -->
        <div v-show="activeTab === 'tracker'">
          <div class="row g-5">

            <div class="col-md-6">
              <label class="form-label fw-semibold">Max Distance (px)
                <span class="badge badge-light-primary ms-2">{{ config.tracker_max_distance }}</span>
              </label>
              <input type="range" class="form-range" min="20" max="300" step="5"
                v-model.number="config.tracker_max_distance" />
              <div class="text-muted fs-8 mt-1">Jarak maksimum antar frame untuk menganggap objek sama.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Max Lost Frames
                <span class="badge badge-light-primary ms-2">{{ config.tracker_max_lost_frames }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="30" step="1"
                v-model.number="config.tracker_max_lost_frames" />
              <div class="text-muted fs-8 mt-1">Frame hilang sebelum track dihapus.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Velocity Damping
                <span class="badge badge-light-primary ms-2">{{ config.tracker_velocity_damping }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="1.0" step="0.05"
                v-model.number="config.tracker_velocity_damping" />
              <div class="text-muted fs-8 mt-1">Faktor peluruhan kecepatan (1.0 = tidak meluruh).</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Distance Growth Factor
                <span class="badge badge-light-primary ms-2">{{ config.tracker_distance_growth }}</span>
              </label>
              <input type="range" class="form-range" min="0" max="1.0" step="0.05"
                v-model.number="config.tracker_distance_growth" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Max Distance Cap (px)
                <span class="badge badge-light-primary ms-2">{{ config.tracker_max_distance_cap }}</span>
              </label>
              <input type="range" class="form-range" min="50" max="500" step="10"
                v-model.number="config.tracker_max_distance_cap" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">BBox Smooth Alpha
                <span class="badge badge-light-primary ms-2">{{ config.bbox_smooth_alpha }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="1.0" step="0.05"
                v-model.number="config.bbox_smooth_alpha" />
              <div class="text-muted fs-8 mt-1">Faktor EMA untuk smoothing bounding box (1.0 = tanpa smooth).</div>
            </div>

            <div class="col-12"><div class="separator separator-dashed my-2"></div></div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Kalman Filter</label>
              <div class="form-check form-switch form-check-custom form-check-solid mt-2">
                <input class="form-check-input" type="checkbox" v-model="config.use_kalman_filter" />
                <label class="form-check-label fw-semibold text-gray-700">
                  {{ config.use_kalman_filter ? 'Aktif' : 'Nonaktif' }}
                </label>
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Tracker Prediction</label>
              <div class="form-check form-switch form-check-custom form-check-solid mt-2">
                <input class="form-check-input" type="checkbox" v-model="config.use_tracker_prediction" />
                <label class="form-check-label fw-semibold text-gray-700">
                  {{ config.use_tracker_prediction ? 'Aktif' : 'Nonaktif' }}
                </label>
              </div>
            </div>

            <template v-if="config.use_kalman_filter">
              <div class="col-md-4">
                <label class="form-label fw-semibold fs-8">Kalman Process Noise Pos
                  <span class="badge badge-light-info ms-1">{{ config.kalman_process_noise_pos }}</span>
                </label>
                <input type="range" class="form-range" min="0.1" max="10" step="0.1"
                  v-model.number="config.kalman_process_noise_pos" />
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold fs-8">Kalman Process Noise Vel
                  <span class="badge badge-light-info ms-1">{{ config.kalman_process_noise_vel }}</span>
                </label>
                <input type="range" class="form-range" min="0.1" max="20" step="0.5"
                  v-model.number="config.kalman_process_noise_vel" />
              </div>
              <div class="col-md-4">
                <label class="form-label fw-semibold fs-8">Kalman Measurement Noise
                  <span class="badge badge-light-info ms-1">{{ config.kalman_measurement_noise }}</span>
                </label>
                <input type="range" class="form-range" min="0.1" max="20" step="0.5"
                  v-model.number="config.kalman_measurement_noise" />
              </div>
            </template>
          </div>
        </div>

        <!-- ── COUNTING TAB ── -->
        <div v-show="activeTab === 'counting'">
          <div class="row g-5">

            <div class="col-12"><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Gate & Threshold</h6></div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Center Gate Margin Ratio
                <span class="badge badge-light-primary ms-2">{{ config.center_gate_margin_ratio }}</span>
              </label>
              <input type="range" class="form-range" min="0.05" max="0.4" step="0.01"
                v-model.number="config.center_gate_margin_ratio" />
              <div class="text-muted fs-8 mt-1">Proporsi lebar frame untuk zona deteksi gate tengah.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Counting Confidence Threshold
                <span class="badge badge-light-primary ms-2">{{ config.counting_confidence_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                v-model.number="config.counting_confidence_threshold" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Min Displacement (px)
                <span class="badge badge-light-primary ms-2">{{ config.counting_min_displacement_px }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="50" step="1"
                v-model.number="config.counting_min_displacement_px" />
              <div class="text-muted fs-8 mt-1">Pergerakan minimum yang dihitung sebagai hitungan valid.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Cooldown per Track (detik)
                <span class="badge badge-light-primary ms-2">{{ config.tracker_count_cooldown_seconds }}s</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="5" step="0.1"
                v-model.number="config.tracker_count_cooldown_seconds" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Global Count Min Interval (detik)
                <span class="badge badge-light-primary ms-2">{{ config.global_count_min_interval_seconds }}s</span>
              </label>
              <input type="range" class="form-range" min="0.01" max="1" step="0.01"
                v-model.number="config.global_count_min_interval_seconds" />
            </div>

            <div class="col-12"><div class="separator separator-dashed my-2"></div><h6 class="text-muted fw-semibold text-uppercase fs-8 mb-0">Temporal Vote Pool</h6></div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">Pending Vote Frames (min hit)
                <span class="badge badge-light-primary ms-2">{{ config.pending_vote_frames }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="5" step="1"
                v-model.number="config.pending_vote_frames" />
              <div class="text-muted fs-8 mt-1">Minimal deteksi dalam window untuk dianggap valid.</div>
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">Pending Vote Window (frame)
                <span class="badge badge-light-primary ms-2">{{ config.pending_vote_window }}</span>
              </label>
              <input type="range" class="form-range" min="2" max="10" step="1"
                v-model.number="config.pending_vote_window" />
              <div class="text-muted fs-8 mt-1">Ukuran jendela vote (frame). Lebih kecil = lebih responsif.</div>
            </div>

            <div class="col-md-4">
              <label class="form-label fw-semibold">Pending Max Distance (px)
                <span class="badge badge-light-primary ms-2">{{ config.pending_max_distance }}</span>
              </label>
              <input type="range" class="form-range" min="5" max="100" step="5"
                v-model.number="config.pending_max_distance" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Count Allow Prediction</label>
              <div class="form-check form-switch form-check-custom form-check-solid mt-2">
                <input class="form-check-input" type="checkbox" v-model="config.count_allow_prediction" />
                <label class="form-check-label fw-semibold text-gray-700">
                  {{ config.count_allow_prediction ? 'Aktif' : 'Nonaktif' }}
                </label>
              </div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Relaxed Confidence Threshold
                <span class="badge badge-light-primary ms-2">{{ config.count_relaxed_confidence_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="0.1" max="0.9" step="0.05"
                v-model.number="config.count_relaxed_confidence_threshold" />
            </div>
          </div>
        </div>

        <!-- ── BACKGROUND TAB ── -->
        <div v-show="activeTab === 'background'">
          <div class="row g-5">

            <div class="col-md-6">
              <label class="form-label fw-semibold">Background Subtractor Type</label>
              <select v-model="config.bg_subtractor_type" class="form-select">
                <option value="MOG2">MOG2 (Mixture of Gaussians v2)</option>
                <option value="KNN">KNN (K-Nearest Neighbours)</option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Learning Rate
                <span class="badge badge-light-primary ms-2">{{ config.bg_learning_rate }}</span>
              </label>
              <input type="range" class="form-range" min="0.0001" max="0.01" step="0.0001"
                v-model.number="config.bg_learning_rate" />
              <div class="text-muted fs-8 mt-1">Kecepatan adaptasi background. Rendah = stabil, Tinggi = cepat beradaptasi.</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Morphology Kernel Size
                <span class="badge badge-light-primary ms-2">{{ config.morphology_kernel_size }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="11" step="2"
                v-model.number="config.morphology_kernel_size" />
              <div class="text-muted fs-8 mt-1">Ukuran kernel erosi/dilasi (harus ganjil).</div>
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Morph Close Kernel Size
                <span class="badge badge-light-primary ms-2">{{ config.morph_close_kernel_size }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="21" step="2"
                v-model.number="config.morph_close_kernel_size" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Frame Difference Threshold
                <span class="badge badge-light-primary ms-2">{{ config.frame_difference_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="5" max="100" step="5"
                v-model.number="config.frame_difference_threshold" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Motion History Frames
                <span class="badge badge-light-primary ms-2">{{ config.motion_history_frames }}</span>
              </label>
              <input type="range" class="form-range" min="1" max="20" step="1"
                v-model.number="config.motion_history_frames" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">NMS IoU Threshold (Contour)
                <span class="badge badge-light-primary ms-2">{{ config.nms_iou_threshold }}</span>
              </label>
              <input type="range" class="form-range" min="0.01" max="0.5" step="0.01"
                v-model.number="config.nms_iou_threshold" />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">NMS Merge Distance (px)
                <span class="badge badge-light-primary ms-2">{{ config.nms_merge_distance }}</span>
              </label>
              <input type="range" class="form-range" min="5" max="100" step="5"
                v-model.number="config.nms_merge_distance" />
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Saved Toast -->
    <div v-if="savedToast" class="position-fixed bottom-0 end-0 p-3" style="z-index:9999">
      <div class="toast show align-items-center text-white bg-success border-0">
        <div class="d-flex">
          <div class="toast-body">
            <i class="bi bi-check-circle me-2"></i> Pengaturan berhasil disimpan!
          </div>
        </div>
      </div>
    </div>

    <!-- Error Toast -->
    <div v-if="errorToast" class="position-fixed bottom-0 end-0 p-3" style="z-index:9999">
      <div class="toast show align-items-center text-white bg-danger border-0">
        <div class="d-flex">
          <div class="toast-body">
            <i class="bi bi-exclamation-circle me-2"></i> {{ errorToast }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { DeviceStorageService } from "@/services/DeviceStorageService";
import { deviceApiService } from "@/services/DeviceApiService";

const STORAGE_KEY_PREFIX = "iot_device_config_";

const DEFAULT_CONFIG = {
  // Model
  use_yolo: true,
  yolo_model_type: 'contour_motion',
  yolo_conf_threshold: 0.35,
  yolo_nms_threshold: 0.45,
  yolo_input_size: 320,
  // Color preprocessing
  color_brightness: 0,
  color_contrast: 0,
  color_saturation: 0,
  color_hue: 0,
  color_gamma: 1.0,
  color_use_clahe: false,
  color_clahe_clip: 2.0,
  // Detection
  min_contour_area: 200,
  max_contour_area: 10000,
  confidence_threshold: 0.50,
  display_confidence_threshold: 0.45,
  shape_aspect_min: 0.35,
  shape_aspect_max: 2.5,
  shape_min_solidity: 0.30,
  shape_min_compactness: 0.08,
  shape_min_extent: 0.18,
  confidence_weight_area: 0.25,
  confidence_weight_aspect: 0.20,
  confidence_weight_solidity: 0.20,
  confidence_weight_compactness: 0.15,
  confidence_weight_darkness: 0.20,
  // Tracker
  tracker_max_distance: 120,
  tracker_max_lost_frames: 10,
  tracker_distance_growth: 0.45,
  tracker_max_distance_cap: 220,
  tracker_velocity_damping: 0.85,
  tracker_min_confidence_keep: 0.2,
  bbox_smooth_alpha: 0.35,
  use_tracker_prediction: false,
  use_kalman_filter: true,
  kalman_process_noise_pos: 1.0,
  kalman_process_noise_vel: 4.0,
  kalman_measurement_noise: 4.0,
  // Counting
  center_gate_margin_ratio: 0.1,
  counting_confidence_threshold: 0.52,
  counting_min_displacement_px: 6,
  tracker_count_cooldown_seconds: 0.9,
  global_count_min_interval_seconds: 0.08,
  count_allow_prediction: true,
  count_max_lost_frames: 2,
  count_recent_detection_window: 6,
  count_prediction_min_speed: 4.5,
  count_relaxed_confidence_threshold: 0.4,
  pending_vote_frames: 2,
  pending_vote_window: 3,
  pending_max_distance: 25,
  // Background
  bg_subtractor_type: "MOG2",
  bg_learning_rate: 0.0008,
  morphology_kernel_size: 3,
  morph_close_kernel_size: 7,
  frame_difference_threshold: 20,
  motion_history_frames: 5,
  nms_iou_threshold: 0.10,
  nms_merge_distance: 20,
  // Contour Motion model params
  cm_min_area: 12,
  cm_max_area: 700,
  cm_diff_threshold: 12,
};

export default defineComponent({
  name: "iot-settings",
  setup() {
    const activeTab = ref("model");
    const selectedDeviceId = ref<number | "global">("global");
    const devices = ref(DeviceStorageService.loadDevices());
    const config = ref({ ...DEFAULT_CONFIG });
    const savedToast = ref(false);
    const errorToast = ref("");
    const saving = ref(false);

    const currentModel = computed({
      get: () => {
        if (!config.value.use_yolo) return "contour";
        return config.value.yolo_model_type || "yolov8n";
      },
      set: (val: string) => {
        if (val === "contour") {
          config.value.use_yolo = false;
          config.value.yolo_model_type = "contour";
        } else {
          config.value.use_yolo = true;
          config.value.yolo_model_type = val;
        }
      },
    });

    const confidenceWeights = {
      confidence_weight_area: "Area",
      confidence_weight_aspect: "Aspect Ratio",
      confidence_weight_solidity: "Solidity",
      confidence_weight_compactness: "Compactness",
      confidence_weight_darkness: "Darkness",
    };

    const totalConfidenceWeight = computed(() =>
      ["confidence_weight_area", "confidence_weight_aspect", "confidence_weight_solidity",
       "confidence_weight_compactness", "confidence_weight_darkness"]
        .reduce((sum, k) => sum + (config.value[k] || 0), 0)
    );

    const storageKey = computed(() =>
      selectedDeviceId.value === "global"
        ? `${STORAGE_KEY_PREFIX}global`
        : `${STORAGE_KEY_PREFIX}${selectedDeviceId.value}`
    );

    const loadConfig = async () => {
      // Always start with localStorage cache for instant render
      try {
        const stored = localStorage.getItem(storageKey.value);
        config.value = stored
          ? { ...DEFAULT_CONFIG, ...JSON.parse(stored) }
          : { ...DEFAULT_CONFIG };
      } catch {
        config.value = { ...DEFAULT_CONFIG };
      }
      // For specific devices, fetch latest from API (overwrites local cache)
      if (selectedDeviceId.value !== "global") {
        try {
          const remote = await deviceApiService.getDeviceConfig(selectedDeviceId.value as number);
          if (remote !== null) {
            config.value = { ...DEFAULT_CONFIG, ...remote };
            localStorage.setItem(storageKey.value, JSON.stringify(config.value));
          }
        } catch {
          // API unavailable — keep local cache silently
        }
      }
    };

    const onDeviceChange = () => loadConfig();

    const saveSettings = async () => {
      saving.value = true;
      try {
        localStorage.setItem(storageKey.value, JSON.stringify(config.value));
        // Sync to API for specific devices
        if (selectedDeviceId.value !== "global") {
          await deviceApiService.updateDeviceConfig(selectedDeviceId.value as number, config.value);
        }
        savedToast.value = true;
        setTimeout(() => { savedToast.value = false; }, 3000);
      } catch (err: any) {
        const msg = err?.message || "Gagal menyimpan ke server";
        errorToast.value = msg;
        setTimeout(() => { errorToast.value = ""; }, 4000);
      } finally {
        saving.value = false;
      }
    };

    const resetToDefault = () => {
      config.value = { ...DEFAULT_CONFIG };
    };

    const exportConfig = () => {
      // Export full config — all fields are now supported by the RPi codebase
      const blob = new Blob([JSON.stringify(config.value, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const deviceLabel = selectedDeviceId.value === "global" ? "global" : `device_${selectedDeviceId.value}`;
      a.href = url;
      a.download = `config_${deviceLabel}.json`;
      a.click();
      URL.revokeObjectURL(url);
    };

    onMounted(async () => {
      setCurrentPageBreadcrumbs("Pengaturan IoT", ["IoT"]);
      // Load real devices from API; fall back to localStorage cache
      try {
        const response = await deviceApiService.listDevices({
          per_page: 100,
          sort_by: "id",
          sort_order: "asc",
        });
        devices.value = response.data;
        DeviceStorageService.saveDevices(response.data);
      } catch {
        devices.value = DeviceStorageService.loadDevices();
      }
      loadConfig();
    });

    return {
      activeTab,
      selectedDeviceId,
      devices,
      config,
      currentModel,
      savedToast,
      errorToast,
      saving,
      confidenceWeights,
      totalConfidenceWeight,
      onDeviceChange,
      saveSettings,
      resetToDefault,
      exportConfig,
    };
  },
});
</script>
